/* eslint-disable no-plusplus */
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Paper, Typography } from '@material-ui/core';

import Loading from '../../../../components/CustomComponents/Loading';
import KPTextform from '../../../../components/KPTComponents/KPTextform';

import { formActions } from '../../../../actions';
import { isQcvCouncil } from '../../../../utils/permission';
import {
	qcvScoreBreakdown,
	qcvAutoPriorityScore,
	QCV_SECTION_CAPS,
	QCV_TYPICAL_WORK_ROWS,
	QCV_TYPICAL_WORK_MAX,
} from '../../../../utils/qcv.score';

// 7 loại công trình của mục A (đồng bộ thứ tự & tiêu đề với form Đăng ký hồ sơ /
// HoSoViewAdmin). Điểm từng loại = tổng điểm các thành tích đã chấm của loại đó.
const KHCN_TYPE_LIST = [
	{ key: 'formSolution', title: 'Bằng độc quyền sáng chế, giải pháp hữu ích' },
	{ key: 'formScientificResearch', title: 'Bài báo khoa học (tạp chí quốc tế, quốc gia)' },
	{ key: 'formScientificReport', title: 'Bài báo khoa học (hội thảo quốc tế, quốc gia)' },
	{ key: 'formResearchProject', title: 'Chương trình, dự án, đề tài nghiên cứu khoa học' },
	{ key: 'formMonograph', title: 'Sách chuyên khảo, giáo trình, chương sách' },
	{ key: 'formTechCompany', title: 'Sản phẩm khoa học công nghệ được ứng dụng' },
	{ key: 'formReward', title: 'Giải thưởng, huy chương đã đạt được' },
];

export default function HoSoNote() {
	const dispatch = useDispatch();

	const role = useSelector(state => state.userReducer.user?.role);
	const userId = useSelector(state => state.userReducer.user?.id);
	const loading = useSelector(state => state.formReducer.loading);
	const applicationList = useSelector(state => state.formReducer.applicationList);

	const [values, setValues] = useState({
		disabledScore: 0,
		ethnicMinorityScore: 0,
	});
	
	const arrayPath = window.location.href.split('/');
	const urlId = parseInt(arrayPath.slice(arrayPath.length -1));
	const applicationDetail = applicationList.find((e) => e.id === urlId);
	
	// Lưu giá trị
	useEffect(() => {
		if (applicationDetail) {
			var tw = {};
			try { tw = JSON.parse(applicationDetail?.typicalWork || '{}') || {}; } catch (e) { tw = {}; }
			// Điểm ưu tiên (D) tự động: mỗi nội dung ứng viên tích = 5 điểm (tối đa 10).
			// Mặc định điền tự động; nếu admin đã chấm/ghi đè trước đó thì giữ giá trị đã lưu.
			const autoScoreD = qcvAutoPriorityScore(applicationDetail?.priority);
			setValues({
				...values,
				note: applicationDetail?.note,
				scoreA: applicationDetail?.scoreA,
				scoreB: applicationDetail?.scoreB,
				scoreC: applicationDetail?.scoreC,
				scoreD: applicationDetail?.scoreD || autoScoreD,
				tw1: tw?.tw1 ?? 0,
				tw2: tw?.tw2 ?? 0,
				tw3: tw?.tw3 ?? 0,
				tw4: tw?.tw4 ?? 0,
			});
		}
	}, [applicationList]);

	// JSON 4 điểm con công trình tiêu biểu (8.1-8.4) gửi kèm khi lưu
	const typicalWorkPayload = () => JSON.stringify({
		tw1: values?.tw1 || 0,
		tw2: values?.tw2 || 0,
		tw3: values?.tw3 || 0,
		tw4: values?.tw4 || 0,
	});

	// Hội đồng BÌNH CHỌN: xem được toàn bộ điểm nhưng KHÔNG sửa, KHÔNG duyệt.
	const readOnly = isQcvCouncil(role);

	const saveNote = () => readOnly || dispatch(formActions.updateBCD({
		...values,
		typicalWork: typicalWorkPayload(),
		id: urlId,
		status: 'need_update',
	}));

	// Chặn cả ở đây, không chỉ ẩn nút: KPTextform bọc trong <form onSubmit>, nếu
	// chỉ ẩn nút thì gõ Enter trong ô nhập vẫn submit được.
	const handleSubmit = () => readOnly || dispatch(formActions.updateBCD({
		...values,
		typicalWork: typicalWorkPayload(),
		id: urlId,
		status: 'approved',
	}));
	
	// Điểm tự động từng loại công trình (A) + điểm ưu tiên tự chấm (D).
	const khcnByType = KHCN_TYPE_LIST.map((t) => ({
		...t,
		score: Math.round(
			(applicationDetail?.[t.key] || []).reduce((s, e) => s + (e?.score || 0), 0) * 100
		) / 100,
	}));
	const autoScoreD = qcvAutoPriorityScore(applicationDetail?.priority);

	const sectionLabel = (text) => (
		<Typography variant='subtitle2' style={{ marginTop: 4 }}><b>{text}</b></Typography>
	);
	const noteLine = (text) => (
		<Typography variant='body2' style={{ color: '#555' }}>{text}</Typography>
	);

	var textForm = [
		// ===== A. Công trình tiêu biểu =====
		{ key: 'a-label', width: 12, value: true, render: (
			sectionLabel('A. Chấm điểm Công trình tiêu biểu (mục A.8 – tối đa 10 điểm):')
		) },
		// 7 loại công trình + điểm tính được của từng loại (tự động, chỉ xem)
		{ key: 'a-types', width: 12, value: true, render: (
			<Box style={{ marginBottom: 4 }}>
				{khcnByType.map((t, i) => (
					<Box
						key={t.key}
						display='flex'
						justifyContent='space-between'
						alignItems='center'
						style={{ padding: '3px 0', borderBottom: '1px dashed #eee' }}
					>
						<Typography variant='body2' style={{ paddingRight: 8 }}>{`${i + 1}. ${t.title}`}</Typography>
						<Typography variant='body2'><b>{t.score}</b></Typography>
					</Box>
				))}
			</Box>
		) },
		{ key: 'a8-label', width: 12, value: true, render: (
			<Typography variant='body2' style={{ marginTop: 4 }}>
				<b>{`A.8 Công trình tiêu biểu (tối đa ${QCV_TYPICAL_WORK_MAX} điểm):`}</b>
			</Typography>
		) },
		// Nhãn & trần điểm lấy từ QCV_TYPICAL_WORK_ROWS để khớp hệt file xuất điểm.
		...QCV_TYPICAL_WORK_ROWS.map((r) => ({
			key: r.key,
			width: 12,
			type: 'float',
			label: `${r.code} ${r.label} (0-${r.max})`,
			value: values?.[r.key],
		})),
		// ===== B. Khen thưởng =====
		{ key: 'b-label', width: 12, value: true, render: (
			<Box style={{ marginTop: 8 }}>
				{sectionLabel('B. Điểm thành tích khen thưởng (tối đa 8 điểm):')}
				{noteLine('+ Bằng khen cấp Nhà nước: 8 điểm')}
				{noteLine('+ Bằng khen Bộ, ban, ngành, tỉnh: 6 điểm')}
			</Box>
		) },
		{ key: 'scoreB', width: 12, label: 'Điểm chấm (giám khảo)', type: 'float', value: values?.scoreB },
		// ===== C. Hoạt động cộng đồng =====
		{ key: 'c-label', width: 12, value: true, render: (
			<Box style={{ marginTop: 8 }}>
				{sectionLabel('C. Điểm thành tích hoạt động cộng đồng (tối đa 7 điểm):')}
				{noteLine('+ Hoạt động bồi dưỡng nhân lực (chia sẻ tri thức, chuyển giao KHCN, tập huấn...) trong lĩnh vực xét giải: 3-5 điểm')}
				{noteLine('+ Hoạt động tình nguyện vì cộng đồng nói chung: 1-2 điểm')}
			</Box>
		) },
		{ key: 'scoreC', width: 12, label: 'Điểm chấm (giám khảo)', type: 'float', value: values?.scoreC },
		// ===== D. Ưu tiên =====
		{ key: 'd-label', width: 12, value: true, render: (
			<Box style={{ marginTop: 8 }}>
				{sectionLabel('D. Ưu tiên (tối đa 10 điểm):')}
				{noteLine(`Điểm tự chấm: ${autoScoreD} điểm (mỗi nội dung ưu tiên 5 điểm)`)}
			</Box>
		) },
		{ key: 'scoreD', width: 12, label: 'Điểm chấm (giám khảo)', type: 'float', value: values?.scoreD },
		{ key: 'note', width: 12, height: 10, label: 'Ghi chú của ban thư ký', value: values?.note },
		{ key: 'button-2', width: 6, label: 'Không duyệt hồ sơ', type: 'button', onClick: saveNote },
		{ key: 'submit', width: 6, label: 'Duyệt hồ sơ', type: 'submit' },
	];

	if (!role || role === 'candidate') {
		// Hồ sơ phải thuộc về người dùng đã đăng nhập và trong tình trạng cần phải sửa
		if (applicationDetail?.status !== 'need_update' || applicationDetail?.profile?.id !== userId) return null;
		else textForm = [{ key: 'note', width: 12, height: 10, value: values?.note, disabled: true }]
	}

	// Chỉ đọc: bỏ 2 nút "Không duyệt hồ sơ"/"Duyệt hồ sơ" và khoá hết ô nhập.
	// Các dòng chỉ render (nhãn mục, bảng tách điểm) không bị ảnh hưởng vì
	// KPTextfield trả về props.render trước khi xét disabled.
	if (readOnly) {
		textForm = textForm
			.filter((e) => e.type !== 'button' && e.type !== 'submit')
			.map((e) => ({ ...e, disabled: true }));
	}

  // Điểm A/B/C/D + tổng: dùng CHUNG hàm với "Xuất file chấm"
  // (utils/qcv.score.js) để hai nơi không bao giờ lệch nhau nữa.
  const breakdown = useMemo(
    () => (applicationDetail ? qcvScoreBreakdown(applicationDetail, values) : null),
    [applicationDetail, values]
  );

  const score = breakdown?.total ?? null;

  const breakdownRows = [
    { label: "A. Thành tích khoa học công nghệ", value: breakdown?.scoreA, cap: null },
    { label: "B. Thành tích khen thưởng", value: breakdown?.scoreB, cap: QCV_SECTION_CAPS.scoreB },
    { label: "C. Hoạt động cộng đồng", value: breakdown?.scoreC, cap: QCV_SECTION_CAPS.scoreC },
    { label: "D. Ưu tiên", value: breakdown?.scoreD, cap: QCV_SECTION_CAPS.scoreD },
  ];

	return (
		// Bố cục DỌC để vừa cột chấm điểm sticky bên phải (sidebar hẹp).
		<Paper style={{ padding: 12 }}>
			<Typography variant='h6' style={{ textAlign: 'center', fontWeight: 700 }}>
				CHẤM ĐIỂM HỒ SƠ
			</Typography>
			<Box display='flex' flexDirection='column' justifyContent='center' alignItems='center' style={{ padding: '8px 0' }}>
				<Typography variant='h2' style={{ color: '#ee6064' }}>
					<b>{score}</b>
				</Typography>
				<Typography variant='subtitle1'>Tổng điểm của ứng viên</Typography>
			</Box>
			<Box width='100%' style={{ marginBottom: 12 }}>
				{breakdownRows.map((row) => (
					<Box
						key={row.label}
						display='flex'
						justifyContent='space-between'
						alignItems='center'
						style={{ padding: '6px 0', borderBottom: '1px solid #eee' }}
					>
						<Typography variant='body2'>{row.label}</Typography>
						<Typography variant='body2'>
							<b>{Math.round((row.value || 0) * 100) / 100}</b>
							{row.cap ? <span style={{ color: '#999' }}>{` / ${row.cap}`}</span> : null}
						</Typography>
					</Box>
				))}
				<Box display='flex' justifyContent='space-between' style={{ padding: '8px 0' }}>
					<Typography variant='subtitle1'><b>Tổng điểm (A+B+C+D)</b></Typography>
					<Typography variant='subtitle1' style={{ color: '#ee6064' }}><b>{score}</b></Typography>
				</Box>
			</Box>
			<KPTextform
				textForm={textForm}
				handleSubmit={handleSubmit}
				values={values}
				setValues={setValues}
			/>
			<Loading open={loading} />
		</Paper>
	)
}